<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import VideoModalBase from './VideoModalBase.vue'

const CATEGORIES: Record<string, { colour: string }> = {
  person: { colour: '#FFEAEC' }, // red
  rocket: { colour: '#EEF9E6' }, // green
  study: { colour: '#FBE4B0' }, // orange
  journal: { colour: '#D9ECFE' }, // blue
  books: { colour: '#D9ECFE' }, // blue
  market: { colour: '#EEF9E6' }, // green
  child: { colour: '#EEF9E6' }, // green
  webinar: { colour: '#D9ECFE' }, // blue
  audio: { colour: '#FBE4B0' }, // orange
  games: { colour: '#FBE4B0' }, // orange
  tails: { colour: '#D9ECFE' }, // blue
  stars: { colour: '#EEF9E6' } // green
}

type DemoCategory = string

const TYPES: Record<string, { title: string; demoButtonLabel: string; demoUrl: string }> = {
  teacher: {
    title: 'МирГоворит для учителя',
    demoButtonLabel: 'Войти в демо-кабинет учителя',
    demoUrl: '/teacher/teacher_dashboard'
  },
  parent: {
    title: 'МирГоворит для родителя',
    demoButtonLabel: 'Войти в демо-кабинет родителя',
    demoUrl: '/parent/cabinet'
  },
  // TODO: not implemented yet
  child: {
    title: 'МирГоворит для ребёнка',
    demoButtonLabel: 'Войти в демо-кабинет ребёнка',
    demoUrl: ''
  },
  // TODO: not implemented yet
  school: {
    title: 'МирГоворит для школы',
    demoButtonLabel: 'Войти в демо-кабинет школы',
    demoUrl: ''
  }
}

type DemoType = string
type DemoVideo = { id: number; name: string; url: string }
type DemoPlaylistItem = { category_name: DemoCategory; videos: DemoVideo[] }

const props = withDefaults(
  defineProps<{
    modelValue?: DemoType | string
    playlist?:
    | DemoPlaylistItem[]
    | Array<{ category_name: string; videos: Array<{ id?: number; name: string; url: string }> }>
  }>(),
  {
    modelValue: undefined,
    playlist: () => []
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: null]
}>()

const selectedVideoId = ref(-1)

watch(
  () => props.playlist,
  () => {
    if (!props.playlist.length) {
      selectedVideoId.value = -1
      return
    }

    selectedVideoId.value = props.playlist[0].videos[0].id ?? -1
  }
)

const videoSrc = computed(() => {
  for (const cat of props.playlist) {
    const video = cat.videos.find(item => item.id === selectedVideoId.value)
    if (video) return video.url
  }

  return null
})
</script>

<template>
  <VideoModalBase
    class="public-demo-modal"
    :title="modelValue ? TYPES[modelValue].title : ''"
    playlist-first
    :model-value="!!modelValue"
    :video-src="videoSrc ?? undefined"
    @update:model-value="() => emit('update:modelValue', null)"
  >
    <template #playlist>
      <ul class="public-demo-modal__playlist">
        <li
          v-for="(cat, i) in playlist"
          :key="i"
          class="public-demo-modal__playlist-item"
          :style="{ background: CATEGORIES[cat.category_name].colour }"
        >
          <!--
          <div class="public-demo-modal__playlist-metadata">
            <v-icon size="24" class="blue-text" v-if="sect.id === i">mdi-play</v-icon>
            <span v-else>{{ sect.duration | formatDuration }}</span>
          </div>
          -->
          <v-icon size="40" class="mb-1"> main-page/demo-categories/{{ cat.category_name }} </v-icon>
          <ul class="public-demo-modal__playlist-item-body">
            <li
              v-for="item in cat.videos"
              :key="item.id"
              :class="{ 'blue-text': selectedVideoId === item.id }"
              @click="
                () => {
                  selectedVideoId = item.id ?? -1
                }
              "
              v-text="item.name"
            />
          </ul>
        </li>
      </ul>
    </template>

    <template v-if="modelValue === 'teacher'" #description>
      * Для получения полного доступа к функционалу кабинета учителя необходимо пройти базовый семинар по методу
      обучения детей 2–10 лет английскому языку по системе Валерии Мещеряковой I LOVE ENGLISH.
    </template>

    <template v-if="modelValue" #actions>
      <div class="flex-adaptive">
        <v-button
          v-if="modelValue && TYPES[modelValue].demoUrl"
          mode="primary flat large"
          :href="modelValue ? TYPES[modelValue].demoUrl : undefined"
        >
          {{ modelValue ? TYPES[modelValue].demoButtonLabel : '' }}
        </v-button>
      </div>
    </template>
  </VideoModalBase>
</template>

<style lang="scss">
.public-demo-modal {
  &__playlist {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__playlist-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    border-radius: 8px;

    // cursor: pointer;
    position: relative;

    /*
    transition: opacity 0.1s ease;
    &:not(&_selected):hover {
      opacity: 0.8;
    }

    &_selected {
      border: 2px solid $blue;
      cursor: default;
    }
    */
  }

  &__playlist-metadata {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;

    padding: 0 8px;
    border-radius: 4px;
    background: white;

    position: absolute;
    top: 10px;
    right: 10px;

    display: flex;
  }

  &__playlist-item-body {
    display: flex;
    flex-direction: column;
    text-align: center;

    & > * {
      margin-bottom: 4px;
      cursor: pointer;
    }
  }
}
</style>
